import { Card } from "@/components/ui/card";
import { useMemo } from "react";
import { tokenizeWithFormat } from "@/lib/formatDiff";

/**
 * Word-by-word diff view with optional formatting-aware layer.
 *
 * Colour legend:
 *   - green   → correct word AND (if format-aware) correct formatting
 *   - red     → wrong spelling / typo
 *   - amber   → missing word (present in source, absent in typing)
 *   - orange  → extra word (typed but not in source)
 *   - yellow  → correct spelling but WRONG formatting (bold/italic/underline/align mismatch)
 *
 * When the caller passes `sourceHtml` + `typedHtml`, each correct word is
 * cross-referenced against the source's formatting context and any deviation
 * (missed bold, extra italic, wrong alignment) is rendered with a yellow
 * ring + a small BIU/align badge. This makes the "formatting mistake"
 * category actionable at the exact word position, not just as an aggregate.
 *
 * Props:
 *   result       — {words: [{word, status, typed?}]}
 *   sourceHtml   — original source (optional; enables formatting layer)
 *   typedHtml    — user-typed HTML (optional; enables formatting layer)
 *   showLegend   — render the colour legend
 */
export default function DiffView({ result, sourceHtml, typedHtml, showLegend = true }) {
  const formatAware = !!(sourceHtml && typedHtml);

  // Tokenise once. Only the *correct* words in `result.words` map to real
  // positions in `sourceHtml`; missing/extra tokens throw off the alignment,
  // so we walk both streams in step and re-anchor whenever we hit them.
  const { srcTokens, usrTokens } = useMemo(() => {
    if (!formatAware) return { srcTokens: [], usrTokens: [] };
    return { srcTokens: tokenizeWithFormat(sourceHtml), usrTokens: tokenizeWithFormat(typedHtml) };
  }, [formatAware, sourceHtml, typedHtml]);

  if (!result) return null;

  const colorFor = (s, fmt) => {
    if (s === "correct" && fmt && fmt.length) {
      return "bg-amber-100 text-amber-900 border-amber-400 ring-1 ring-amber-500/40";
    }
    switch (s) {
      case "correct":   return "bg-green-100 text-green-800 border-green-300";
      case "incorrect": return "bg-red-100 text-red-800 border-red-300 line-through";
      case "missing":   return "bg-yellow-100 text-yellow-800 border-yellow-300 italic";
      case "extra":     return "bg-orange-100 text-orange-800 border-orange-300";
      default:          return "bg-secondary/60 text-foreground border-neutral-300";
    }
  };

  // Walk word list and pair positions with their format token when possible.
  let srcIdx = 0;
  let usrIdx = 0;

  return (
    <div data-testid="diff-view">
      {showLegend && (
        <div className="flex flex-wrap gap-2 mb-4 text-[11px] font-mono" data-testid="diff-legend">
          <span className="px-2 py-1 border border-green-300 bg-green-100 text-green-800 rounded">correct</span>
          <span className="px-2 py-1 border border-red-300 bg-red-100 text-red-800 line-through rounded">wrong spelling</span>
          <span className="px-2 py-1 border border-yellow-300 bg-yellow-100 text-yellow-800 italic rounded">missing word</span>
          <span className="px-2 py-1 border border-orange-300 bg-orange-100 text-orange-800 rounded">extra word</span>
          {formatAware && (
            <span className="px-2 py-1 border border-amber-400 bg-amber-100 text-amber-900 ring-1 ring-amber-500/40 rounded">
              formatting mismatch (B / I / U / align)
            </span>
          )}
        </div>
      )}
      <Card className="rounded-lg border-border p-6 font-mono leading-loose text-base bg-card">
        {result.words.map((w, i) => {
          let fmt = [];
          if (formatAware) {
            const s = w.status === "extra" ? null : srcTokens[srcIdx];
            const u = w.status === "missing" ? null : usrTokens[usrIdx];
            if (s && u) {
              if (s.bold !== u.bold) fmt.push(u.bold ? "+B" : "−B");
              if (s.italic !== u.italic) fmt.push(u.italic ? "+I" : "−I");
              if (s.underline !== u.underline) fmt.push(u.underline ? "+U" : "−U");
              if (s.align !== u.align) fmt.push(`align:${s.align}→${u.align}`);
            }
            if (w.status !== "extra") srcIdx++;
            if (w.status !== "missing") usrIdx++;
          }
          const title =
            w.status === "incorrect" ? `you typed: ${w.typed}` :
            w.status === "correct" && fmt.length ? `formatting off: ${fmt.join(", ")}` :
            w.status;
          return (
            <span
              key={i}
              className={`inline-flex items-center gap-1 m-0.5 px-1.5 py-0.5 border rounded text-sm ${colorFor(w.status, fmt)}`}
              title={title}
              data-testid={`diff-word-${i}`}
              data-fmt-mismatch={fmt.length ? "1" : "0"}
            >
              <span>{w.word}</span>
              {fmt.length > 0 && (
                <sup className="text-[9px] font-bold uppercase tracking-widest bg-amber-500 text-white px-1 rounded">
                  {fmt.join("·")}
                </sup>
              )}
            </span>
          );
        })}
      </Card>
    </div>
  );
}
